import { useEffect, useId, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useDebouncedCallback } from "use-debounce";
import { getRangeBounds, rangesEqual, type RangeValue } from "./rangeValues";

type RangeDraft = { low: string; high: string };

type ParsedRangeDraft =
  | { valid: false }
  | { valid: true; range: RangeValue | undefined };

const parseRangeBound = (text: string): number | undefined => {
  if (text.trim() === "") {
    return undefined;
  }
  return Number(text);
};

const isValidRangeBound = (
  bound: number | undefined,
  max: number | undefined,
) => {
  if (bound === undefined) {
    return true;
  }
  if (!Number.isFinite(bound) || bound < 0) {
    return false;
  }
  return max === undefined || bound <= max;
};

const parseRangeDraft = (draft: RangeDraft, max?: number): ParsedRangeDraft => {
  const low = parseRangeBound(draft.low);
  const high = parseRangeBound(draft.high);

  if (!isValidRangeBound(low, max)) {
    return { valid: false };
  }
  if (!isValidRangeBound(high, max)) {
    return { valid: false };
  }

  if (low !== undefined && high !== undefined) {
    if (low > high) {
      return { valid: false };
    }
    return { valid: true, range: { type: "between", low, high } };
  }
  if (low !== undefined) {
    return { valid: true, range: { type: "minimum", low } };
  }
  if (high !== undefined) {
    return { valid: true, range: { type: "maximum", high } };
  }
  return { valid: true, range: undefined };
};

export type RangeInputsProps = {
  label: string;
  value: RangeValue | undefined;
  prefix?: string;
  suffix?: string;
  max?: number;
  disabled?: boolean;
  /** Trailing delay in milliseconds; Enter and blur apply immediately. Defaults to 500. */
  debounceMs?: number;
  className?: string;
  errorClassName?: string;
  onChange: (value: RangeValue | undefined) => void;
};

export const RangeInputs = ({
  label,
  value,
  prefix,
  suffix,
  max,
  disabled = false,
  debounceMs = 500,
  className,
  errorClassName,
  onChange,
}: RangeInputsProps) => {
  const id = useId();
  const bounds = getRangeBounds(value);
  const [draft, setDraft] = useState(() => ({
    appliedValue: value,
    low: bounds.low?.toString() ?? "",
    high: bounds.high?.toString() ?? "",
  }));

  const displayedDraft = rangesEqual(draft.appliedValue, value)
    ? draft
    : {
        appliedValue: value,
        low: bounds.low?.toString() ?? "",
        high: bounds.high?.toString() ?? "",
      };

  const debouncedChange = useDebouncedCallback(
    (next: RangeValue | undefined) => {
      setDraft((current) => ({ ...current, appliedValue: next }));
      onChange(next);
    },
    debounceMs,
  );

  useEffect(() => {
    return () => debouncedChange.cancel();
  }, [bounds.low, bounds.high, disabled, debouncedChange]);

  const parsed = parseRangeDraft(displayedDraft, max);

  const updateDraft = (bound: "low" | "high", text: string) => {
    const next = { ...displayedDraft, [bound]: text };

    setDraft(next);

    const result = parseRangeDraft(next, max);

    debouncedChange.cancel();

    if (result.valid) {
      debouncedChange(result.range);
    }
  };

  return (
    <div className={className}>
      <div
        className="flex items-center gap-2"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            debouncedChange.flush();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            debouncedChange.flush();
          }
        }}
      >
        {(["low", "high"] as const).map((bound, index) => (
          <div key={bound} className="contents">
            {index > 0 && <span className="text-sm text-zinc-700">to</span>}
            <label
              className={twMerge(
                "flex min-w-0 flex-1 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-2 text-sm focus-within:border-purple-600",
                disabled && "opacity-50",
              )}
            >
              <span className="sr-only">
                {bound === "low" ? "Minimum" : "Maximum"} {label}
              </span>
              {prefix && <span className="text-zinc-400">{prefix}</span>}
              <input
                aria-invalid={!parsed.valid}
                aria-describedby={!parsed.valid ? `${id}-error` : undefined}
                className="w-full min-w-0 appearance-none bg-transparent outline-none placeholder:text-zinc-400 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                disabled={disabled}
                inputMode="decimal"
                onChange={(event) => updateDraft(bound, event.target.value)}
                placeholder={bound === "low" ? "Min" : "Max"}
                type="text"
                value={displayedDraft[bound]}
              />
              {suffix && <span className="text-zinc-400">{suffix}</span>}
            </label>
          </div>
        ))}
      </div>
      {!parsed.valid && (
        <p
          id={`${id}-error`}
          className={twMerge("mt-2 text-xs text-red-600", errorClassName)}
        >
          Maximum must be greater than minimum
        </p>
      )}
    </div>
  );
};
